/*
 * Cross-fade a stack of slides in place -- images in the hero and story
 * frames, quotes in the pull-quote.
 *
 * The contract a caller implements: a container carrying `data-ge-rotator`
 * and `data-interval` (seconds), slides classed `ge-rotator__slide`, and
 * `is-active` rendered on the first of them server-side. Only that class
 * moves from here; the fade and the hiding are CSS, and how the slides are
 * stacked is the caller's own business.
 *
 * The timer runs only while the stack is on screen -- a home page carries
 * three of these and none needs to tick while the visitor is in the footer.
 *
 * A stack holding one slide is left alone, so callers need not gate the
 * attribute, and prefers-reduced-motion stops the cycling entirely rather
 * than merely shortening it: the movement is the thing being opted out of,
 * not its speed.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function start(frame) {
    const slides = [...frame.querySelectorAll('.ge-rotator__slide')];
    if (slides.length < 2) return;

    const interval = (parseFloat(frame.dataset.interval) || 5) * 1000;
    let index = slides.findIndex((s) => s.classList.contains('is-active'));
    if (index < 0) index = 0;
    let timer = null;

    /*
     * Slides after the first are rendered without their image URLs (see
     * snippets/ge-image-rotator.liquid) so they are not all fetched on load.
     * Each is given its URLs one step ahead -- when the one before it
     * becomes active -- so it has a full interval to arrive before it fades
     * in. Quote slides carry no images and pass straight through.
     */
    const hydrate = (slide) => {
      if (!slide) return;
      const imgs = slide.matches('img') ? [slide] : [...slide.querySelectorAll('img')];
      imgs.forEach((img) => {
        if (img.dataset.geSrcset) {
          img.srcset = img.dataset.geSrcset;
          delete img.dataset.geSrcset;
        }
        if (img.dataset.geSrc) {
          img.src = img.dataset.geSrc;
          delete img.dataset.geSrc;
        }
      });
    };

    const step = () => {
      slides[index].classList.remove('is-active');
      index = (index + 1) % slides.length;
      slides[index].classList.add('is-active');
      hydrate(slides[(index + 1) % slides.length]);
    };

    const run = () => {
      if (timer !== null || reduced.matches) return;
      hydrate(slides[(index + 1) % slides.length]);
      timer = setInterval(step, interval);
    };
    const halt = () => {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    };

    let onScreen = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            onScreen = e.isIntersecting;
            if (onScreen) run();
            else halt();
          }),
        { rootMargin: '100px 0px' }
      ).observe(frame);
    } else {
      run();
    }

    // Honour the preference if it is changed after load -- but only resume a
    // stack that is actually on screen, or it would tick unobserved until the
    // next time the visitor happens to scroll past it.
    reduced.addEventListener('change', () => (reduced.matches || !onScreen ? halt() : run()));
  }

  document.querySelectorAll('[data-ge-rotator]').forEach(start);
})();
